<script setup>
import { onMounted, reactive, ref, computed } from 'vue';
import emailjs from "@emailjs/browser";
import useVuelidate from '@vuelidate/core';
import { required, email, minLength } from '@vuelidate/validators';
import { Icon } from '@iconify/vue';
import AOS from 'aos';

const state = reactive({
  name: '',
  email: '',
  message: ''
});

const rules = computed(() => ({
  name: { required },
  email: { required, email },
  message: { required, minLength: minLength(20) }
}));

const v$ = useVuelidate(rules, state);
const isSubmitting = ref(false);
const toast = reactive({
  show: false,
  message: '',
  type: 'success' // 'success' or 'error'
});

function showToast(message, type = 'success') {
  toast.message = message;
  toast.type = type;
  toast.show = true;
  setTimeout(() => {
    toast.show = false;
  }, 4000);
}

async function submitForm() {
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) return;

  isSubmitting.value = true;

  try {
    await emailjs.sendForm('service_x0vrhv2', 'template_6ucaqca', '#myform', 'polQPG6B-ulsOOaj4');
    showToast('Message sent successfully!', 'success');
    // Reset form
    state.name = '';
    state.email = '';
    state.message = '';
    v$.value.$reset();
  } catch (error) {
    console.error('Email failed to send:', error);
    showToast('Failed to send message. Please try again.', 'error');
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(() => {
  AOS.init({
    duration: 800,
    once: true,
  });
});
</script>

<template>
  <div class="page-wrapper">
    <div class="hero-section">
      <div class="hero-overlay"></div>
      <h1 class="title" data-aos="fade-up">{{ $t('contact-page.home.title') }}</h1>
    </div>

    <main class="main-content">
      <div class="contact-container" data-aos="fade-up" data-aos-delay="200">
        <div class="info-panel">
          <div class="info-content">
            <h2>{{ $t('contact-page.body.get_form.credentials.h2') }}</h2>
            <p class="info-subtitle">{{ $t( 'We are here to help. Reach out to us through any of the following channels.') }}</p>
            <div class="contact-details">
              <div class="contact-item">
                <Icon icon="material-symbols:phone-in-talk-outline-rounded" class="contact-icon" />
                <span>+237 674-646-019</span>
              </div>
              <div class="contact-item">
                <Icon icon="material-symbols:mail-outline-rounded" class="contact-icon" />
                <span>hozalogisticsconsultants@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
        <div class="form-panel">
          <p class="form-intro">{{ $t('contact-page.body.get_form.form.send') }}</p>
          <form @submit.prevent="submitForm" id="myform" novalidate>
            <div class="form-grid">
              <div class="form-group">
                <label for="name">{{ $t('contact-page.body.get_form.form.tex.name.label') }}</label>
                <input type="text" id="name" name="name" :placeholder="$t('contact-page.body.get_form.form.tex.name.placeholder')" v-model="state.name" :class="{ 'input-error': v$.name.$error }">
                <div class="error-message" v-if="v$.name.$error">{{ v$.name.$errors[0].$message }}</div>
              </div>
              <div class="form-group">
                <label for="email">{{ $t('contact-page.body.get_form.form.tex.email.label') }}</label>
                <input type="email" id="email" name="email" :placeholder="$t('contact-page.body.get_form.form.tex.email.placeholder')" v-model="state.email" :class="{ 'input-error': v$.email.$error }">
                <div class="error-message" v-if="v$.email.$error">{{ v$.email.$errors[0].$message }}</div>
              </div>
            </div>
            <div class="form-group">
              <label for="message">{{ $t('contact-page.body.get_form.form.message.label') }}</label>
              <textarea id="message" name="message" :placeholder="$t('contact-page.body.get_form.form.message.placeholder')" v-model="state.message" :class="{ 'input-error': v$.message.$error }"></textarea>
              <div class="error-message" v-if="v$.message.$error">{{ v$.message.$errors[0].$message }}</div>
            </div>
            <button type="submit" class="submit-btn" :disabled="isSubmitting">
              <span v-if="isSubmitting">{{ $t('contact-page.submitting', 'Sending...') }}</span>
              <span v-else>{{ $t('contact-page.body.get_form.form.submit') }}</span>
            </button>
          </form>
        </div>
      </div>
    </main>

    <Transition name="toast-fade">
      <div v-if="toast.show" class="toast-notification" :class="`toast-${toast.type}`">
        {{ toast.message }}
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.hero-section {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50vh;
  background: url("../assets/rectangle-36@2x.webp") center center;
  background-size: cover;
  position: relative;
  text-align: center;
}
.hero-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(10, 45, 97, 0.6);
}
.title{
  color: white;
  font-size: 3rem;
  text-transform: uppercase;
  position: relative;
  z-index: 2;
  padding-bottom: 0.5rem;
}
.title::after{
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: #fcbc0d;
}

.main-content {
  padding: 6rem 5%;
  background-color: #f8f9fa;
}

.contact-container {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  max-width: 1200px;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.info-panel {
  background-color: #0A2D61;
  color: white;
  padding: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.info-panel h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-subtitle {
  opacity: 0.8;
  line-height: 1.6;
  margin-bottom: 3rem;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
}

.contact-icon {
  font-size: 1.5rem;
}

.form-panel {
  padding: 50px;
}

.form-intro {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

input, textarea {
  width: 100%;
  padding: 14px;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

input:focus, textarea:focus {
  outline: none;
  border-color: #0A2D61;
  box-shadow: 0 0 0 3px rgba(10, 45, 97, 0.1);
}

textarea {
  min-height: 120px;
  resize: vertical;
}

.input-error {
  border-color: #e53e3e;
}

.error-message {
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 8px;
}

.submit-btn {
  width: 100%;
  padding: 16px;
  background-color: #0A2D61;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  background-color: #1c4b8d;
}

.submit-btn:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}

.toast-notification {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 8px;
  color: white;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  z-index: 2000;
}
.toast-success {
  background-color: #2f855a;
}
.toast-error {
  background-color: #c53030;
}
.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.toast-fade-enter-from, .toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}

@media (max-width: 992px) {
  .contact-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .title {
    font-size: 2.2rem;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>